import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { openWhatsAppConsultation } from '../utils/whatsapp';
import ServiceCard from '../components/home/ServiceCard';
import FeatureBox from '../components/ui/FeatureBox';
import ProcessStep from '../components/ui/ProcessStep';
import CTASection from '../components/ui/CTASection';
import PortfolioSlider from '../components/ui/PortfolioSlider';
import TestimonialsSlider from '../components/ui/TestimonialsSlider';
import { homeContent } from '../content/home';
import './Home.css';

const Home = () => {
  const scrollToServices = (e) => {
    e.preventDefault();
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>نمو ماركتينج - شريكك الرقمي للنجاح</title>
        <meta
          name="description"
          content="وكالة تسويق رقمية متخصصة في تصميم المواقع، إدارة السوشيال ميديا، والإعلانات الممولة. نساعدك تنمّي مشروعك وتوصل لعملائك باحترافية."
        />
      </Helmet>

      <div className="home-page">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="container">
            <div className="hero-content">
              <div className="hero-text">
                <h1 className="hero-title">{homeContent.hero.title}</h1>
                <p className="hero-subtitle">{homeContent.hero.subtitle}</p>
                <div className="hero-buttons">
                  <button
                    className="btn btn-primary btn-large"
                    onClick={openWhatsAppConsultation}
                  >
                    {homeContent.hero.primaryButton}
                  </button>
                  <a
                    href="#services"
                    className="btn btn-secondary btn-large"
                    onClick={scrollToServices}
                  >
                    {homeContent.hero.secondaryButton}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeContent.servicesSection.title}</h2>
              <p className="section-subtitle">{homeContent.servicesSection.subtitle}</p>
            </div>
            <div className="grid grid-3">
              {homeContent.servicesSection.services.map((service) => (
                <ServiceCard
                  key={service.id}
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  link={service.link}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeContent.whyUs.title}</h2>
            </div>
            <div className="grid grid-4">
              {homeContent.whyUs.features.map((feature) => (
                <FeatureBox
                  key={feature.id}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeContent.ourApproach.title}</h2>
              <p className="section-subtitle">{homeContent.ourApproach.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {homeContent.ourApproach.approaches.map((approach) => (
                <FeatureBox
                  key={approach.id}
                  icon={approach.icon}
                  title={approach.title}
                  description={approach.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeContent.howWeWork.title}</h2>
            </div>
            <div className="grid grid-5-auto">
              {homeContent.howWeWork.steps.map((step) => (
                <ProcessStep
                  key={step.id}
                  number={step.number}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Guarantees */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeContent.guarantees.title}</h2>
              <p className="section-subtitle">{homeContent.guarantees.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {homeContent.guarantees.items.map((item) => (
                <FeatureBox
                  key={item.id}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Counter */}
        {homeContent.stats && (
          <section className="section bg-primary">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title" style={{ color: 'var(--white)' }}>
                  {homeContent.stats.title}
                </h2>
              </div>
              <div className="stats-grid">
                {homeContent.stats.counters.map((stat) => {
                  const StatIcon = stat.icon;
                  return (
                    <div key={stat.id} className="stat-box">
                      <div className="stat-icon">
                        <StatIcon />
                      </div>
                      <div className="stat-number">
                        {stat.number}
                        {stat.suffix}
                      </div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Portfolio Slider */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeContent.portfolio.title}</h2>
              <p className="section-subtitle">{homeContent.portfolio.subtitle}</p>
            </div>
            <PortfolioSlider slides={homeContent.portfolio.projects} />
          </div>
        </section>

        {/* Testimonials */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeContent.testimonials.title}</h2>
              <p className="section-subtitle">{homeContent.testimonials.subtitle}</p>
            </div>
            <TestimonialsSlider reviews={homeContent.testimonials.reviews} />
          </div>
        </section>

        {/* Final CTA */}
        <CTASection
          title={homeContent.finalCTA.title}
          subtitle={homeContent.finalCTA.subtitle}
          buttonText={homeContent.finalCTA.buttonText}
          backgroundStyle={homeContent.finalCTA.backgroundStyle}
        />
      </div>
    </>
  );
};

export default Home;
